import { Video } from "lucide-react";
import { useGetList, useTranslate } from "ra-core";
import { Link } from "react-router";
import { isTeamsJoinUrl } from "../domain/deals/dealConfig";
import { formatDemoDate } from "../domain/format";
import type { GavionDeal } from "../deals/types";
import { addDays, torontoDate, torontoMidnight } from "../domain/time/toronto";

/** "Mes démos à venir": next 7 days, with Teams links (validated join URLs only). */
export const UpcomingDemos = ({
  salesId,
  now,
}: {
  salesId: number;
  now: Date;
}) => {
  const translate = useTranslate();
  const today = torontoDate(now);
  // Day-stable query window (today 00:00 to day+8 00:00 Toronto); the ticking `now` trims past demos client side.
  const { data = [] } = useGetList<GavionDeal>(
    "deals",
    {
      filter: {
        sales_id: salesId,
        "demo_at@gte": torontoMidnight(today).toISOString(),
        "demo_at@lt": torontoMidnight(addDays(today, 8)).toISOString(),
      },
      sort: { field: "demo_at", order: "ASC" },
      pagination: { page: 1, perPage: 20 },
    },
    { retry: false },
  );
  const limit = now.getTime() + 7 * 86_400_000;
  const demos = data.filter((d) => {
    const at = d.demo_at ? Date.parse(d.demo_at) : Number.NaN;
    return at >= now.getTime() && at <= limit;
  });
  return (
    <section
      aria-labelledby="gavion-demos"
      className="rounded-xl border bg-card p-4"
    >
      <h2 id="gavion-demos" className="mb-3 text-lg font-bold">
        {translate("gavion.dashboard.demos_title")}
      </h2>
      {demos.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("gavion.dashboard.no_demos")}
        </p>
      ) : (
        <ul className="divide-y">
          {demos.map((d) => (
            <li
              key={d.id}
              className="flex items-center justify-between gap-3 py-2 text-sm"
            >
              <span className="min-w-0">
                <span className="mr-2 tabular-nums text-muted-foreground">
                  {formatDemoDate(d.demo_at as string)}
                </span>
                <Link
                  to={`/deals/${d.id}/show`}
                  className="font-medium hover:underline"
                >
                  {d.name}
                </Link>
              </span>
              {isTeamsJoinUrl(d.teams_join_url) && (
                <a
                  href={d.teams_join_url as string}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex shrink-0 items-center gap-1 font-medium text-accent-foreground underline-offset-2 hover:underline"
                >
                  <Video className="size-4" aria-hidden="true" />
                  Teams
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
